import type { MapEventData } from '../spacetime/mapEvents';
import { MAP_EVENT_TYPE } from '../spacetime/mapEvents';
import { t } from '../i18n';
import { windowId, windowRegistry } from './windowing';
import type { WindowHandle } from './windowing/WindowRegistry';

type DebugTab = 'events';

export class DebugWindow {
    private readonly container: HTMLElement;
    private readonly contentArea: HTMLElement;
    private readonly closeBtn: HTMLButtonElement;
    private readonly tabBar: HTMLElement;
    private readonly tabButtons: Map<DebugTab, HTMLButtonElement> = new Map();

    private mapEvents: MapEventData[] = [];
    private activeTab: DebugTab = 'events';
    private defaultPosition = { x: 0, y: 0 };
    private readonly defaultSize = { width: 720, height: 420 };
    private readonly MIN_WIDTH = 520;
    private readonly MIN_HEIGHT = 280;
    private readonly windowHandle: WindowHandle;

    constructor(container: HTMLElement, closeBtn: HTMLButtonElement) {
        this.container = container;
        const header = this.container.querySelector<HTMLElement>('.window-header');
        const resizeHandle = this.container.querySelector<HTMLElement>('.window-resize-handle');
        const contentArea = this.container.querySelector<HTMLElement>('.window-content');
        if (!header || !resizeHandle || !contentArea) {
            throw new Error('Debug window is missing drag/resize elements');
        }
        this.contentArea = contentArea;
        this.closeBtn = closeBtn;
        this.centerWindow();
        this.windowHandle = windowRegistry.register({
            id: windowId('debug-window', 'main'),
            container: this.container as HTMLDivElement,
            header: header as HTMLDivElement,
            resizeHandle: resizeHandle as HTMLDivElement,
            closeButton: this.closeBtn,
            minWidth: this.MIN_WIDTH,
            minHeight: this.MIN_HEIGHT,
            defaultPosition: this.defaultPosition,
            defaultSize: this.defaultSize,
        });
        const rawTab = this.windowHandle.getActiveTab();
        const restoredTab = rawTab === 'events' ? rawTab : null;
        if (restoredTab === 'events') {
            this.activeTab = restoredTab;
        }

        this.tabBar = this.buildTabBar();
        header.after(this.tabBar);

        this.closeBtn.addEventListener('click', () => this.hide());
        if (this.windowHandle.getWindowState() === 'open') {
            this.show();
        } else {
            this.hide();
        }
    }

    public updateMapEvents(events: MapEventData[]): void {
        this.mapEvents = events.slice().sort((a, b) => {
            const tickDiff = b.tick - a.tick;
            if (tickDiff !== 0) return tickDiff;
            const seqDiff = this.eventSequence(b.eventId) - this.eventSequence(a.eventId);
            if (seqDiff !== 0) return seqDiff;
            return a.eventId.localeCompare(b.eventId);
        });
        if (this.activeTab === 'events') this.render();
    }

    public show(): void {
        if (!this.container.hidden) return;
        this.container.hidden = false;
        this.windowHandle.setWindowState('open');
        this.render();
    }

    public hide(): void {
        if (this.container.hidden) return;
        this.container.hidden = true;
        this.windowHandle.setWindowState('closed');
    }

    public get isVisible(): boolean {
        return !this.container.hidden;
    }

    public openTab(tab: DebugTab): void {
        this.setActiveTab(tab);
        this.show();
    }

    public isOpenOnTab(tab: DebugTab): boolean {
        return this.isVisible && this.activeTab === tab;
    }

    private buildTabBar(): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'char-tab-bar';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = t('debug.tabs.events');
        btn.className = 'char-tab-btn';
        btn.addEventListener('click', () => this.setActiveTab('events'));
        bar.appendChild(btn);
        this.tabButtons.set('events', btn);

        this.updateTabStyles();
        return bar;
    }

    private setActiveTab(tab: DebugTab): void {
        this.activeTab = tab;
        this.windowHandle.setActiveTab(tab);
        this.updateTabStyles();
        this.render();
    }

    private updateTabStyles(): void {
        for (const [key, btn] of this.tabButtons) {
            if (key === this.activeTab) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        }
    }

    private render(): void {
        if (!this.isVisible) return;
        this.contentArea.innerHTML = '';
        this.renderEvents();
    }

    private renderEvents(): void {
        if (this.mapEvents.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'char-tab-empty';
            empty.textContent = t('debug.noEvents');
            this.contentArea.appendChild(empty);
            return;
        }

        const table = document.createElement('table');
        table.className = 'events-table';
        Object.assign(table.style, {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.78rem',
            color: '#e2e8f0',
        });

        const head = document.createElement('thead');
        const headRow = document.createElement('tr');
        ['Tick', 'Time', 'Type', 'Payload'].forEach((label) => {
            const th = document.createElement('th');
            th.textContent = label;
            Object.assign(th.style, {
                textAlign: 'left',
                borderBottom: '1px solid rgba(148,163,184,0.35)',
                padding: '6px 8px',
                position: 'sticky',
                top: '0',
                background: '#0f172a',
                zIndex: '1',
            });
            headRow.appendChild(th);
        });
        head.appendChild(headRow);
        table.appendChild(head);

        const body = document.createElement('tbody');
        for (const event of this.mapEvents.slice(0, 250)) {
            const row = document.createElement('tr');

            const payload: Record<string, unknown> = {};
            if (event.sourceId) payload.sourceId = this.normalizeEventEntityId(event.sourceId);
            if (event.targetId) payload.targetId = this.normalizeEventEntityId(event.targetId);
            if (event.eventType === MAP_EVENT_TYPE.damage) {
                if (event.skillId) payload.skillId = event.skillId;
                payload.amount = event.amount;
                payload.targetHp = event.targetHp;
                payload.targetMaxHp = event.targetMaxHp;
                payload.targetX = event.targetX;
                payload.targetY = event.targetY;
            } else if (event.eventType === MAP_EVENT_TYPE.heal) {
                if (event.skillId) payload.skillId = event.skillId;
                payload.amount = event.amount;
                payload.targetHp = event.targetHp;
                payload.targetMaxHp = event.targetMaxHp;
                payload.targetX = event.targetX;
                payload.targetY = event.targetY;
            } else if (event.eventType === MAP_EVENT_TYPE.itemUse) {
                payload.itemId = event.skillId;
                payload.sourceX = event.sourceX;
                payload.sourceY = event.sourceY;
                payload.targetX = event.targetX;
                payload.targetY = event.targetY;
            } else if (event.eventType === MAP_EVENT_TYPE.move) {
                payload.fromX = event.sourceX;
                payload.fromY = event.sourceY;
                payload.toX = event.targetX;
                payload.toY = event.targetY;
                payload.snap = event.snap;
            } else if (event.eventType === MAP_EVENT_TYPE.entityDied) {
                payload.mob = this.normalizeEventEntityId(event.targetId);
                payload.killedBy = this.normalizeEventEntityId(event.sourceId);
                if (event.skillId) payload.skill = event.skillId;
                payload.x = event.targetX;
                payload.y = event.targetY;
            } else if (event.eventType === MAP_EVENT_TYPE.spawnMobLoot) {
                payload.mob = this.normalizeEventEntityId(event.sourceId);
                payload.x = event.sourceX;
                payload.y = event.sourceY;
                payload.loot = event.payloadJson;
            } else if (event.eventType === MAP_EVENT_TYPE.queueCleared) {
                payload.message = 'event, cancel key received';
                payload.sourceId = this.normalizeEventEntityId(event.sourceId);
                payload.cleared = event.amount;
                payload.tick = event.tick;
                if (event.payloadJson) {
                    payload.details = event.payloadJson;
                }
            } else {
                if (event.skillId) payload.skillId = event.skillId;
                payload.sourceX = event.sourceX;
                payload.sourceY = event.sourceY;
                payload.targetX = event.targetX;
                payload.targetY = event.targetY;
            }

            const tickCell = document.createElement('td');
            tickCell.textContent = String(event.tick);
            const timeCell = document.createElement('td');
            timeCell.textContent = String(this.eventSequence(event.eventId)).slice(-6);
            const typeCell = document.createElement('td');
            typeCell.textContent = event.eventType;
            const payloadCell = document.createElement('td');
            payloadCell.textContent = JSON.stringify(payload);

            row.appendChild(tickCell);
            row.appendChild(timeCell);
            row.appendChild(typeCell);
            row.appendChild(payloadCell);

            for (const cell of [tickCell, timeCell, typeCell, payloadCell]) {
                Object.assign(cell.style, {
                    padding: '5px 8px',
                    borderBottom: '1px solid rgba(148,163,184,0.15)',
                });
            }
            Object.assign(payloadCell.style, {
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            });
            body.appendChild(row);
        }
        table.appendChild(body);

        const wrapper = document.createElement('div');
        Object.assign(wrapper.style, {
            maxHeight: '100%',
            overflow: 'auto',
        });
        wrapper.appendChild(table);
        this.contentArea.appendChild(wrapper);
    }

    private normalizeEventEntityId(entityId: string): string {
        return entityId.startsWith('player:') ? entityId.slice('player:'.length) : entityId;
    }

    private eventSequence(eventId: string): number {
        const parts = eventId.split('-');
        const tail = parts[parts.length - 1];
        const parsed = Number(tail);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private centerWindow(): void {
        this.defaultPosition = {
            x: Math.max(0, Math.round((window.innerWidth - this.defaultSize.width) / 2)),
            y: Math.max(0, Math.round((window.innerHeight - this.defaultSize.height) / 2)),
        };
    }

    public destroy(): void {
        this.windowHandle.destroy();
    }
}
